import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { breakpoint } from '@zocdoc/components/src/grid';
import { white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import Rating from '../Rating';
import {
    OVERALL_RATING_STRINGS,
    BEDSIDE_MANNER_STRINGS,
    WAIT_TIME_STRINGS,
} from '../constants';
import CollapsibleText from '@zocdoc/components/src/CollapsibleText';
import { LOCALIZED_STRINGS } from './constants';
import moment from 'moment';
import VisibilitySensor from 'react-visibility-sensor';


const ReviewWrapper = styled.div`
    background-color: ${white};
    clear: both;
    margin-bottom: 25px;
`;

const TextDiv = styled.div`
    clear: both;
    color: ${p => p.theme.profile.textColor};
    font: 14px/20px ${FontFamily.KievitSlabBook};

    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.KievitSlabBook};
    }
`;

const ByLine = styled.div`
    margin-top: 8px;
    font: 12px/16px ${FontFamily.KievitSlabBook};
    color: ${({ theme }) => theme.profile.textColor};
    display: inline-block;

    ${breakpoint.medium} {
        font: 14px/20px ${FontFamily.KievitSlabBook};
    }
`;

const BulletWrapper = styled.div`
    padding-left: 10px;
    padding-right: 10px;
    display: inline;
`;

const RatingsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-top: ${({ ratingsBelowText }) => ratingsBelowText ? '8px' : 0};
`;

const InvisibleRating = styled.div`
    flex-basis: 20%;
`;

class Review extends PureComponent {
    static propTypes = {
        onScrollIntoView: PropTypes.func,
        overallRating: PropTypes.number.isRequired,
        bedsideRating: PropTypes.number.isRequired,
        waitTimeRating: PropTypes.number.isRequired,
        comment: PropTypes.string,
        date: PropTypes.string,
        patientName: PropTypes.string,
        isPartnerReview: PropTypes.bool,
        isMailedInReview: PropTypes.bool,
        dateRange: PropTypes.string,
        ratingsBelowText: PropTypes.bool,
        index: PropTypes.number,
    };

    constructor(props) {
        super(props);
        this.state = {
            trackScroll: !!props.onScrolledIntoView,
        };
    }

    maybeRenderBedsideRating() {
        const { intl, bedsideRating, ratingsBelowText } = this.props;
        if (bedsideRating) {
            return (
                <Rating
                    horizontal={ratingsBelowText}
                    name={intl.formatMessage(BEDSIDE_MANNER_STRINGS.Label)}
                    rating={bedsideRating}
                />
            );
        }
    }

    maybeRenderWaitimeRating() {
        const { intl, waitTimeRating, ratingsBelowText } = this.props;
        if (waitTimeRating) {
            return (
                <Rating
                    horizontal={ratingsBelowText}
                    name={intl.formatMessage(WAIT_TIME_STRINGS.Label)}
                    rating={waitTimeRating}
                />
            );
        }
    }

    // if there is a date, we show that, if not, we show the dateRange text.
    renderDate() {
        const { date, dateRange } = this.props;
        if (date) {
            // explicitly specify date format: https://momentjs.com/docs/#/parsing/special-formats/
            const dateMoment = moment(date, moment.ISO_8601);
            if (dateMoment.isValid()) {
                const dateFormatted = dateMoment.format('MMMM D, YYYY');
                return [
                    <span
                        key="date"
                        data-test="review-date"
                        itemProp="datePublished"
                    >
                        { dateFormatted }
                    </span>,
                    <BulletWrapper key="bullet">
                        &bull;
                    </BulletWrapper>,
                ];
            }
        }

        const DateRangeEnum = {
            lessThanOneMonth: 'less_than_1_month',
            lessThanThreeMonths: 'less_than_3_months',
            lessThanSixMonths: 'less_than_6_months',
            lessThanOneYear: 'less_than_1_year',
            moreThanOneYear: 'more_than_1_year',
        };


        let dateRangeLocalizedMessage;
        switch (dateRange) {
            case DateRangeEnum.lessThanOneMonth:
                dateRangeLocalizedMessage = LOCALIZED_STRINGS.DateRangeTextLessThan1Month;
                break;
            case DateRangeEnum.lessThanThreeMonths:
                dateRangeLocalizedMessage = LOCALIZED_STRINGS.DateRangeTextLessThan3Months;
                break;
            case DateRangeEnum.lessThanSixMonths:
                dateRangeLocalizedMessage = LOCALIZED_STRINGS.DateRangeTextLessThan6Months;
                break;
            case DateRangeEnum.lessThanOneYear:
                dateRangeLocalizedMessage = LOCALIZED_STRINGS.DateRangeTextLessThan1Year;
                break;
            case DateRangeEnum.moreThanOneYear:
                dateRangeLocalizedMessage = LOCALIZED_STRINGS.DateRangeTextMoreThan1Year;
                break;
        }
        if (dateRangeLocalizedMessage) {
            return [
                <FormattedMessage
                    key="date"
                    {...dateRangeLocalizedMessage}
                    data-test="review-dateRange"
                />,
                <BulletWrapper key="bullet">
                    &bull;
                </BulletWrapper>,
            ];
        }
    }

    renderCommentIfAvailable(comment) {
        if (comment) {
            return (
                <TextDiv itemProp="reviewBody">
                    <CollapsibleText>
                        {comment}
                    </CollapsibleText>
                </TextDiv>
            );
        }
    }

    maybeRenderAuthor() {
        const {
            isPartnerReview,
            isMailedInReview,
            patientName,
        } = this.props;

        if (!isPartnerReview) {
            let message;
            if (!patientName) {
                message = LOCALIZED_STRINGS.InitialsHidden;
            } else if (isMailedInReview) {
                message = LOCALIZED_STRINGS.MailedInByLine;
            } else {
                message = LOCALIZED_STRINGS.VerifiedPatientByLine;
            }

            return [
                <span
                    key="author"
                    data-test="review-author"
                    itemProp="author"
                >
                    <FormattedMessage
                        {...message}
                        values={{ patientName }}
                    />
                </span>,
                <BulletWrapper key="bullet">
                    &bull;
                </BulletWrapper>,
            ];
        }
    }

    renderSource() {
        const {
            isPartnerReview,
        } = this.props;
        const sourceName = isPartnerReview ? 'Press Ganey' : 'Zocdoc';

        return (
            <span data-test="review-source">
                <FormattedMessage
                    {...LOCALIZED_STRINGS.ReviewSource}
                    values={{ sourceName }}
                />
            </span>
        );
    }

    renderCommentsAndMetadata(comment) {
        return (
            <span>
                {this.renderCommentIfAvailable(comment)}
                <ByLine>
                    {this.renderDate()}
                    {this.maybeRenderAuthor()}
                    {this.renderSource()}
                </ByLine>
            </span>
        );
    }

    handleWaypointEnter = visible => {
        const {
            index,
            onScrolledIntoView,
        } = this.props;
        if (visible && !!onScrolledIntoView) {
            this.setState({ trackScroll: false });
            onScrolledIntoView(index);
        }
    }

    render() {
        const {
            intl,
            overallRating,
            comment,
            ratingsBelowText,
        } = this.props;

        const {
            trackScroll,
        } = this.state;

        return (
            <VisibilitySensor
                active={trackScroll}
                onChange={this.handleWaypointEnter}
            >
                <ReviewWrapper
                    data-test="review"
                    itemProp="reviews"
                    itemScope
                    itemType="http://schema.org/Review"
                >
                    {ratingsBelowText && this.renderCommentsAndMetadata(comment)}

                    <RatingsWrapper ratingsBelowText={ratingsBelowText}>
                        <Rating
                            horizontal={ratingsBelowText}
                            name={intl.formatMessage(OVERALL_RATING_STRINGS.Label)}
                            rating={overallRating}
                        />
                        {this.maybeRenderWaitimeRating()}
                        {this.maybeRenderBedsideRating()}
                        <InvisibleRating
                            itemProp="reviewRating"
                            itemScope
                            itemType="http://schema.org/Rating"
                        >
                            <meta
                                content={overallRating}
                                itemProp="ratingValue"
                            />
                            <meta
                                content="1"
                                itemProp="worstRating"
                            />
                            <meta
                                content="5"
                                itemProp="bestRating"
                            />
                        </InvisibleRating>
                    </RatingsWrapper>

                    {!ratingsBelowText && this.renderCommentsAndMetadata(comment)}
                </ReviewWrapper>
            </VisibilitySensor>
        );
    }
}

export default injectIntl(Review);
